/**
 * @fileoverview Route Manifest - Centralized route configuration with metadata
 * @module routes/routeManifest
 *
 * @description
 * Enterprise route manifest defining all application routes with comprehensive
 * metadata including authentication requirements, RBAC roles, lazy loading,
 * prefetch hints, SEO metadata, and breadcrumbs.
 *
 * Features:
 * - Typed route definitions with metadata
 * - Authentication and RBAC support
 * - Lazy loading with React.lazy
 * - Prefetch hints for performance
 * - SEO metadata (title, description)
 * - Breadcrumb navigation
 * - Layout assignments
 * - Route grouping
 * - TypeScript type safety
 *
 * @example
 * ```typescript
 * import { ROUTE_MANIFEST, getRouteByPath } from '@/routes/routeManifest';
 *
 * const route = getRouteByPath('/app/dashboard');
 * console.log(route.meta.title); // "Dashboard"
 * console.log(route.meta.requiresAuth); // true
 * ```
 */

import { lazy } from 'react';
import type { ComponentType, LazyExoticComponent } from 'react';
import { PATHS } from '@/constants/routes';

/**
 * Route metadata interface
 */
export interface RouteMeta {
  /**
   * Page title for document.title and breadcrumbs
   */
  title: string;

  /**
   * Page description for SEO
   */
  description?: string;

  /**
   * Requires authentication to access
   */
  requiresAuth: boolean;

  /**
   * Required roles for RBAC (if empty, any authenticated user can access)
   */
  roles?: string[];

  /**
   * Layout to use for this route
   */
  layout?: 'default' | 'auth' | 'minimal' | 'dashboard';

  /**
   * Prefetch this route for better performance
   */
  prefetch?: boolean;

  /**
   * Show in navigation menu
   */
  showInNav?: boolean;

  /**
   * Icon for navigation (lucide-react icon name)
   */
  icon?: string;

  /**
   * Breadcrumb items
   */
  breadcrumbs?: Array<{ label: string; path?: string }>;

  /**
   * SEO keywords
   */
  keywords?: string[];

  /**
   * Custom metadata
   */
  [key: string]: unknown;
}

/**
 * Route definition interface
 */
export interface RouteDefinition {
  /**
   * Unique route identifier
   */
  id: string;

  /**
   * Route path (use PATHS constants)
   */
  path: string;

  /**
   * Lazy-loaded component
   */
  element: LazyExoticComponent<ComponentType<any>>;

  /**
   * Route metadata
   */
  meta: RouteMeta;

  /**
   * Child routes
   */
  children?: RouteDefinition[];

  /**
   * Index route (renders at parent path)
   */
  index?: boolean;
}

/**
 * Public routes (no authentication required)
 */
export const PUBLIC_ROUTES: RouteDefinition[] = [
  {
    id: 'landing',
    path: PATHS.ROOT,
    element: lazy(() => import('@/pages/Landing')),
    meta: {
      title: 'Welcome',
      description: 'Enterprise React boilerplate with 30+ component groups',
      requiresAuth: false,
      layout: 'minimal',
      prefetch: true,
      keywords: ['react', 'boilerplate', 'enterprise', 'typescript'],
    },
  },
  {
    id: 'getting-started',
    path: '/getting-started',
    element: lazy(() =>
      import('@/pages/GettingStarted').then(({ GettingStartedPage }) => ({
        default: GettingStartedPage,
      }))
    ),
    meta: {
      title: 'Getting Started',
      description: 'Quick start guide for the Enterprise React boilerplate',
      requiresAuth: false,
      layout: 'minimal',
      prefetch: false,
      keywords: ['getting-started', 'guide', 'tutorial', 'setup'],
    },
  },
  {
    id: 'not-found',
    path: PATHS.NOT_FOUND,
    element: lazy(() => import('@/pages/404')),
    meta: {
      title: '404 - Page Not Found',
      description: 'The page you are looking for does not exist',
      requiresAuth: false,
      layout: 'minimal',
    },
  },
];

/**
 * Authentication routes
 */
export const AUTH_ROUTES: RouteDefinition[] = [
  {
    id: 'auth-login',
    path: PATHS.LOGIN,
    element: lazy(() => import('@/pages/Auth/Login')),
    meta: {
      title: 'Login',
      description: 'Sign in to your account',
      requiresAuth: false,
      layout: 'auth',
      prefetch: true,
      breadcrumbs: [{ label: 'Home', path: PATHS.ROOT }, { label: 'Login' }],
      accessControl: 'restricted-flow',  // Direct access restricted, must go through registration/verification
    },
  },
  {
    id: 'auth-callback',
    path: PATHS.CALLBACK,
    element: lazy(() => import('@/pages/Auth/Callback')),
    meta: {
      title: 'Authenticating...',
      description: 'Processing authentication',
      requiresAuth: false,
      layout: 'minimal',
    },
  },
  {
    id: 'auth-register',
    path: '/auth/register',
    element: lazy(() => import('@/pages/Auth/Register')),
    meta: {
      title: 'Register',
      description: 'Create a new account',
      requiresAuth: false,
      layout: 'auth',
      breadcrumbs: [{ label: 'Home', path: PATHS.ROOT }, { label: 'Register' }],
    },
  },
  {
    id: 'auth-verify-email',
    path: '/auth/verify-email',
    element: lazy(() => import('@/pages/Auth/VerifyEmail')),
    meta: {
      title: 'Verify Email',
      description: 'Verify your email address',
      requiresAuth: false,
      layout: 'minimal',
    },
  },
  {
    id: 'auth-forgot-password',
    path: '/auth/forgot-password',
    element: lazy(() => import('@/pages/Auth/ForgotPassword')),
    meta: {
      title: 'Forgot Password',
      description: 'Reset your password',
      requiresAuth: false,
      layout: 'auth',
    },
  },
  {
    id: 'auth-reset-password',
    path: '/auth/reset-password',
    element: lazy(() => import('@/pages/Auth/ResetPassword')),
    meta: {
      title: 'Reset Password',
      description: 'Set a new password',
      requiresAuth: false,
      layout: 'auth',
    },
  },
];

/**
 * Protected application routes (require authentication)
 */
export const APP_ROUTES: RouteDefinition[] = [
  {
    id: 'app-dashboard',
    path: PATHS.DASHBOARD,
    element: lazy(() => import('@/pages/App/Dashboard')),
    meta: {
      title: 'Dashboard',
      description: 'Application dashboard with analytics and metrics',
      requiresAuth: true,
      layout: 'dashboard',
      showInNav: true,
      icon: 'LayoutDashboard',
      prefetch: true,
      breadcrumbs: [{ label: 'Dashboard' }],
      keywords: ['dashboard', 'analytics', 'overview'],
    },
  },
  {
    id: 'app-users',
    path: PATHS.USERS,
    element: lazy(() => import('@/pages/App/Users')),
    meta: {
      title: 'Users',
      description: 'Manage users and permissions',
      requiresAuth: true,
      roles: ['admin', 'manager'],
      layout: 'dashboard',
      showInNav: true,
      icon: 'Users',
      prefetch: true,
      breadcrumbs: [{ label: 'Dashboard', path: PATHS.DASHBOARD }, { label: 'Users' }],
      keywords: ['users', 'management', 'permissions'],
    },
  },
  {
    id: 'app-gateway-types',
    path: PATHS.GATEWAY_TYPES,
    element: lazy(() => import('@/pages/App/GatewayTypes')),
    meta: {
      title: 'Gateway Types',
      description: 'Manage gateway type catalog and MQTT defaults',
      requiresAuth: true,
      roles: ['admin', 'manager'],
      layout: 'dashboard',
      showInNav: true,
      icon: 'Router',
      prefetch: true,
      breadcrumbs: [
        { label: 'Dashboard', path: PATHS.DASHBOARD },
        { label: 'Gateway Types' },
      ],
      keywords: ['gateway', 'types', 'catalog', 'mqtt'],
    },
  },
  {
    id: 'app-device-types',
    path: PATHS.DEVICE_TYPES,
    element: lazy(() => import('@/pages/App/DeviceTypes')),
    meta: {
      title: 'Device Types',
      description: 'Manage device type catalog and register metadata',
      requiresAuth: true,
      roles: ['admin', 'manager'],
      layout: 'dashboard',
      showInNav: true,
      icon: 'Cpu',
      prefetch: true,
      breadcrumbs: [
        { label: 'Dashboard', path: PATHS.DASHBOARD },
        { label: 'Device Types' },
      ],
      keywords: ['device', 'types', 'catalog', 'modbus'],
    },
  },
  {
    id: 'app-mqtt',
    path: PATHS.MQTT,
    element: lazy(() => import('@/pages/App/Mqtt')),
    meta: { title: 'MQTT Configurations', description: 'Manage reusable MQTT configurations', requiresAuth: true, roles: ['admin', 'manager'], layout: 'dashboard', showInNav: false, icon: 'Cable', breadcrumbs: [{ label: 'Dashboard', path: PATHS.DASHBOARD }, { label: 'MQTT' }] },
  },
  {
    id: 'app-gateway-type-create', path: PATHS.GATEWAY_TYPE_CREATE, element: lazy(() => import('@/pages/App/GatewayTypes/Create')),
    meta: { title: 'Create Gateway Type', description: 'Create gateway type', requiresAuth: true, roles: ['admin', 'manager'], layout: 'dashboard', breadcrumbs: [{ label: 'Gateway Types', path: PATHS.GATEWAY_TYPES }, { label: 'Create' }] },
  },
  {
    id: 'app-gateway-type-view', path: '/app/gateway-types/:id', element: lazy(() => import('@/pages/App/GatewayTypes/View')),
    meta: { title: 'Gateway Type Details', description: 'View gateway type', requiresAuth: true, roles: ['admin', 'manager'], layout: 'dashboard', breadcrumbs: [{ label: 'Gateway Types', path: PATHS.GATEWAY_TYPES }, { label: 'Details' }] },
  },
  {
    id: 'app-gateway-type-edit', path: '/app/gateway-types/:id/edit', element: lazy(() => import('@/pages/App/GatewayTypes/Edit')),
    meta: { title: 'Edit Gateway Type', description: 'Edit gateway type', requiresAuth: true, roles: ['admin', 'manager'], layout: 'dashboard', breadcrumbs: [{ label: 'Gateway Types', path: PATHS.GATEWAY_TYPES }, { label: 'Edit' }] },
  },
  {
    id: 'app-device-type-create', path: PATHS.DEVICE_TYPE_CREATE, element: lazy(() => import('@/pages/App/DeviceTypes/Create')),
    meta: { title: 'Create Device Type', description: 'Create device type', requiresAuth: true, roles: ['admin', 'manager'], layout: 'dashboard', breadcrumbs: [{ label: 'Device Types', path: PATHS.DEVICE_TYPES }, { label: 'Create' }] },
  },
  {
    id: 'app-device-type-view', path: '/app/device-types/:id', element: lazy(() => import('@/pages/App/DeviceTypes/View')),
    meta: { title: 'Device Type Details', description: 'View device type', requiresAuth: true, roles: ['admin', 'manager'], layout: 'dashboard', breadcrumbs: [{ label: 'Device Types', path: PATHS.DEVICE_TYPES }, { label: 'Details' }] },
  },
  {
    id: 'app-device-type-edit', path: '/app/device-types/:id/edit', element: lazy(() => import('@/pages/App/DeviceTypes/Edit')),
    meta: { title: 'Edit Device Type', description: 'Edit device type', requiresAuth: true, roles: ['admin', 'manager'], layout: 'dashboard', breadcrumbs: [{ label: 'Device Types', path: PATHS.DEVICE_TYPES }, { label: 'Edit' }] },
  },
  {
    id: 'app-mqtt-create',
    path: PATHS.MQTT_CREATE,
    element: lazy(() => import('@/pages/App/Mqtt/Create')),
    meta: { title: 'Create MQTT Configuration', description: 'Create MQTT configuration', requiresAuth: true, roles: ['admin', 'manager'], layout: 'dashboard', breadcrumbs: [{ label: 'Dashboard', path: PATHS.DASHBOARD }, { label: 'MQTT', path: PATHS.MQTT }, { label: 'Create' }] },
  },
  {
    id: 'app-mqtt-details',
    path: '/app/mqtt/:id',
    element: lazy(() => import('@/pages/App/Mqtt/View')),
    meta: { title: 'MQTT Details', description: 'View MQTT configuration', requiresAuth: true, roles: ['admin', 'manager'], layout: 'dashboard', breadcrumbs: [{ label: 'Dashboard', path: PATHS.DASHBOARD }, { label: 'MQTT', path: PATHS.MQTT }, { label: 'Details' }] },
  },
  {
    id: 'app-mqtt-edit',
    path: '/app/mqtt/:id/edit',
    element: lazy(() => import('@/pages/App/Mqtt/Edit')),
    meta: { title: 'Edit MQTT Configuration', description: 'Edit MQTT configuration', requiresAuth: true, roles: ['admin', 'manager'], layout: 'dashboard', breadcrumbs: [{ label: 'Dashboard', path: PATHS.DASHBOARD }, { label: 'MQTT', path: PATHS.MQTT }, { label: 'Edit' }] },
  },
  {
    id: 'app-user-details',
    path: '/app/users/:id',
    element: lazy(() => import('@/pages/App/Users/[id]')),
    meta: {
      title: 'User Details',
      description: 'View and edit user information',
      requiresAuth: true,
      roles: ['admin', 'manager'],
      layout: 'dashboard',
      breadcrumbs: [
        { label: 'Dashboard', path: PATHS.DASHBOARD },
        { label: 'Users', path: PATHS.USERS },
        { label: 'Details' },
      ],
    },
  },
  {
    id: 'app-profile',
    path: '/app/profile',
    element: lazy(() => import('@/pages/App/Profile')),
    meta: {
      title: 'Profile',
      description: 'Your profile and account settings',
      requiresAuth: true,
      layout: 'dashboard',
      showInNav: true,
      icon: 'User',
      breadcrumbs: [{ label: 'Dashboard', path: PATHS.DASHBOARD }, { label: 'Profile' }],
    },
  },
  {
    id: 'app-settings',
    path: PATHS.SETTINGS,
    element: lazy(() => import('@/pages/App/Settings')),
    meta: {
      title: 'Settings',
      description: 'Manage customer module availability',
      requiresAuth: true,
      roles: ['admin', 'manager'],
      layout: 'dashboard',
      showInNav: true,
      icon: 'Settings',
      breadcrumbs: [{ label: 'Dashboard', path: PATHS.DASHBOARD }, { label: 'Settings' }],
    },
  },
];

/**
 * Error routes
 */
export const ERROR_ROUTES: RouteDefinition[] = [
  //   {
  //     id: 'error-unauthorized',
  //     path: PATHS.UNAUTHORIZED,
  //     element: lazy(() => import('@/pages/401')),
  //     meta: {
  //       title: '401 - Unauthorized',
  //       description: 'You do not have permission to access this page',
  //       requiresAuth: false,
  //       layout: 'minimal',
  //     },
  //   },
  //   {
  //     id: 'error-server',
  //     path: PATHS.SERVER_ERROR,
  //     element: lazy(() => import('@/pages/500')),
  //     meta: {
  //       title: '500 - Server Error',
  //       description: 'Something went wrong on our end',
  //       requiresAuth: false,
  //       layout: 'minimal',
  //     },
  //   },
];

/**
 * Complete route manifest
 */
export const ROUTE_MANIFEST: RouteDefinition[] = [
  ...PUBLIC_ROUTES,
  ...AUTH_ROUTES,
  ...APP_ROUTES,
  ...ERROR_ROUTES,
];

/**
 * Get route by path
 *
 * @param path - Route path
 * @returns Route definition or undefined
 *
 * @example
 * ```typescript
 * const route = getRouteByPath('/app/dashboard');
 * console.log(route?.meta.title); // "Dashboard"
 * ```
 */
export function getRouteByPath(path: string): RouteDefinition | undefined {
  return ROUTE_MANIFEST.find((route) => route.path === path);
}

/**
 * Get route by ID
 *
 * @param id - Route ID
 * @returns Route definition or undefined
 *
 * @example
 * ```typescript
 * const route = getRouteById('app-dashboard');
 * console.log(route?.path); // "/app/dashboard"
 * ```
 */
export function getRouteById(id: string): RouteDefinition | undefined {
  return ROUTE_MANIFEST.find((route) => route.id === id);
}

/**
 * Get all routes that require authentication
 *
 * @returns Array of protected routes
 *
 * @example
 * ```typescript
 * const protectedRoutes = getProtectedRoutes();
 * console.log(protectedRoutes.length); // Number of protected routes
 * ```
 */
export function getProtectedRoutes(): RouteDefinition[] {
  return ROUTE_MANIFEST.filter((route) => route.meta.requiresAuth);
}

/**
 * Get all public routes (no authentication required)
 *
 * @returns Array of public routes
 *
 * @example
 * ```typescript
 * const publicRoutes = getPublicRoutes();
 * ```
 */
export function getPublicRoutes(): RouteDefinition[] {
  return ROUTE_MANIFEST.filter((route) => !route.meta.requiresAuth);
}

/**
 * Get routes for navigation menu
 *
 * @returns Array of routes to show in navigation
 *
 * @example
 * ```typescript
 * const navRoutes = getNavigationRoutes();
 * navRoutes.forEach(route => {
 *   console.log(route.meta.title, route.meta.icon);
 * });
 * ```
 */
export function getNavigationRoutes(): RouteDefinition[] {
  return ROUTE_MANIFEST.filter((route) => route.meta.showInNav);
}

/**
 * Get routes that should be prefetched
 *
 * @returns Array of routes to prefetch
 *
 * @example
 * ```typescript
 * const prefetchRoutes = getPrefetchRoutes();
 * prefetchRoutes.forEach(route => {
 *   // Prefetch route component
 * });
 * ```
 */
export function getPrefetchRoutes(): RouteDefinition[] {
  return ROUTE_MANIFEST.filter((route) => route.meta.prefetch);
}

/**
 * Check if user has access to a route based on roles
 *
 * @param route - Route definition
 * @param userRoles - User's roles
 * @returns True if user has access
 *
 * @example
 * ```typescript
 * const route = getRouteByPath('/app/users');
 * const hasAccess = hasRouteAccess(route, ['admin']);
 * console.log(hasAccess); // true
 * ```
 */
export function hasRouteAccess(route: RouteDefinition | undefined, userRoles: string[]): boolean {
  if (!route) return false;

  // Public routes are always accessible
  if (!route.meta.requiresAuth) return true;

  // If no specific roles required, any authenticated user can access
  if (!route.meta.roles || route.meta.roles.length === 0) return true;

  // Check if user has any of the required roles
  return route.meta.roles.some((role) => userRoles.includes(role));
}

/**
 * Get routes by layout
 *
 * @param layout - Layout name
 * @returns Array of routes using the layout
 *
 * @example
 * ```typescript
 * const dashboardRoutes = getRoutesByLayout('dashboard');
 * ```
 */
export function getRoutesByLayout(layout: string): RouteDefinition[] {
  return ROUTE_MANIFEST.filter((route) => route.meta.layout === layout);
}

/**
 * Get breadcrumbs for a route
 *
 * @param path - Route path
 * @returns Breadcrumb items
 *
 * @example
 * ```typescript
 * const breadcrumbs = getBreadcrumbs('/app/users');
 * breadcrumbs.forEach(crumb => {
 *   console.log(crumb.label, crumb.path);
 * });
 * ```
 */
export function getBreadcrumbs(path: string): Array<{ label: string; path?: string }> {
  const route = getRouteByPath(path);
  return route?.meta.breadcrumbs || [];
}

/**
 * Get page title for a route
 *
 * @param path - Route path
 * @param defaultTitle - Default title if route not found
 * @returns Page title
 *
 * @example
 * ```typescript
 * const title = getPageTitle('/app/dashboard', 'App');
 * document.title = title; // "Dashboard"
 * ```
 */
export function getPageTitle(path: string, defaultTitle = 'App'): string {
  const route = getRouteByPath(path);
  return route?.meta.title || defaultTitle;
}

/**
 * Get SEO metadata for a route
 *
 * @param path - Route path
 * @returns SEO metadata
 *
 * @example
 * ```typescript
 * const seo = getRouteSEO('/app/dashboard');
 * console.log(seo.title, seo.description, seo.keywords);
 * ```
 */
export function getRouteSEO(path: string): {
  title: string;
  description?: string;
  keywords?: string[];
} {
  const route = getRouteByPath(path);
  return {
    title: route?.meta.title || 'App',
    description: route?.meta.description,
    keywords: route?.meta.keywords,
  };
}

/**
 * Route groups for easier management
 */
export const ROUTE_GROUPS = {
  PUBLIC: PUBLIC_ROUTES,
  AUTH: AUTH_ROUTES,
  APP: APP_ROUTES,
  ERROR: ERROR_ROUTES,
} as const;

/**
 * Export route count for monitoring
 */
export const ROUTE_STATS = {
  total: ROUTE_MANIFEST.length,
  public: PUBLIC_ROUTES.length,
  auth: AUTH_ROUTES.length,
  app: APP_ROUTES.length,
  error: ERROR_ROUTES.length,
  protected: getProtectedRoutes().length,
  navigation: getNavigationRoutes().length,
  prefetch: getPrefetchRoutes().length,
} as const;
